import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureException } from "../lib/sentryLazy";

// ---------------------------------------------------------------------------
// ChunkErrorBoundary
// ---------------------------------------------------------------------------
// Catches Vite CSS preload failures and stale-asset chunk load failures.
// The legacy JavaScript recovery below handles failures after Vite deploys
// invalidate old hashed asset URLs. When a user's browser tries to lazy-load
// a chunk from a previous build, the CDN returns 404 (or an HTML error page
// whose MIME type doesn't match "application/javascript").
//
// Recovery strategy:
//   1. First failure → auto-reload the page (picks up new asset manifest)
//   2. Second failure (reload already attempted) → show fallback UI
//   3. Non-chunk errors → re-throw so parent error boundaries handle them
//
// The sessionStorage guard key "fp_chunk_reload" prevents infinite reload
// loops. RootLayout clears it on every successful navigation so each new
// page visit gets one fresh reload attempt.
// ---------------------------------------------------------------------------

const RELOAD_KEY = "fp_chunk_reload";
// CSS preload failures are not necessarily stale deployments. Limit automatic
// recovery to once per tab session, independently of RootLayout's navigation
// reset of the legacy chunk guard. Manual retries must not reset this budget.
const CSS_RELOAD_KEY = "fp_css_preload_reload";

function isCssPreloadError(error: unknown): boolean {
  return error instanceof Error && error.message.startsWith("Unable to preload CSS");
}

const CHUNK_ERROR_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "is not a valid JavaScript MIME type",
  "Loading chunk",
  "ChunkLoadError",
  "Importing a module script failed",
  "error loading dynamically imported module",
] as const;

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return CHUNK_ERROR_PATTERNS.some((p) => msg.includes(p.toLowerCase()));
  }
  // Vite sometimes wraps errors with a name but no matching message
  if (
    error &&
    typeof error === "object" &&
    "name" in error &&
    (error as { name: string }).name === "ChunkLoadError"
  ) {
    return true;
  }
  return false;
}

interface Props {
  children: ReactNode;
  /** Optional custom fallback. Receives a retry callback. */
  fallback?: (props: { retry: () => void }) => ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
  isCssError: boolean;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false, isCssError: false };

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      isChunkError: isChunkLoadError(error),
      isCssError: isCssPreloadError(error),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isCssPreloadError(error)) {
      let recovery: "already_attempted" | "storage_unavailable" = "already_attempted";
      let shouldReload = false;
      try {
        if (!sessionStorage.getItem(CSS_RELOAD_KEY)) {
          sessionStorage.setItem(CSS_RELOAD_KEY, "1");
          shouldReload = true;
        }
      } catch {
        // Without a durable guard, automatic reload could loop. Offer explicit
        // retry instead and retain the original CSS failure in monitoring.
        recovery = "storage_unavailable";
      }
      if (shouldReload) {
        window.location.reload();
        return;
      }
      captureException(error, (scope) => {
        scope.setLevel("error");
        scope.setTag("error.type", "css_preload");
        scope.setTag("css.recovery", recovery);
        scope.setExtra("componentStack", info.componentStack);
      });
      return;
    }

    if (!isChunkLoadError(error)) {
      // Not a chunk error — let a parent boundary deal with it
      throw error;
    }

    // First attempt → reload to pick up new asset manifest (silent recovery)
    if (!sessionStorage.getItem(RELOAD_KEY)) {
      sessionStorage.setItem(RELOAD_KEY, "1");
      window.location.reload();
      return;
    }

    // Reload already attempted and failed — log to Sentry at info level.
    // These are expected post-deploy for users with old bundles, not actionable bugs.
    captureException(error, (scope) => {
      scope.setLevel("info");
      scope.setTag("error.type", "chunk_load");
      scope.setTag("chunk.reload_attempted", "true");
      scope.setExtra("componentStack", info.componentStack);
    });

    // Stay on fallback UI (state is already set via getDerivedStateFromError)
  }

  private handleRetry = () => {
    // Preserve the CSS session budget; legacy chunk retries keep their behavior.
    if (!this.state.isCssError) sessionStorage.removeItem(RELOAD_KEY);
    window.location.reload();
  };

  render() {
    if (this.state.hasError && (this.state.isChunkError || this.state.isCssError)) {
      if (this.props.fallback) {
        return this.props.fallback({ retry: this.handleRetry });
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
          <div className="w-14 h-14 rounded-full bg-fp-amber-100 flex items-center justify-center mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-7 h-7 text-fp-amber-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-fp-navy-900 mb-2">
            {this.state.isCssError ? "Page could not finish loading" : "New Version Available"}
          </h2>
          <p className="text-sm text-fp-slate-500 max-w-sm mb-6">
            {this.state.isCssError
              ? "Some page styles could not be loaded. Please try reloading, or return home and try again later."
              : "FairPath was just updated. Please reload to get the latest version."}
          </p>
          <div className="flex gap-3">
            <button
              onClick={this.handleRetry}
              className="px-5 py-2.5 bg-fp-emerald-500 text-white text-sm font-medium rounded-fp hover:bg-fp-emerald-600 transition-colors"
            >
              Reload Page
            </button>
            <a
              href="/"
              className="px-5 py-2.5 bg-white text-fp-navy-900 text-sm font-medium rounded-fp border border-fp-slate-200 hover:bg-fp-slate-50 transition-colors"
            >
              Go Home
            </a>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
